import { type AwsRumConfig, AwsRum } from 'aws-rum-web';
import { createContext, useContext, useMemo, useState } from 'react';
import { getRumConfig, isRumEnabled } from 'src/utils/rum-config';

import { RumContextValue, RumProviderProps } from './RumProvider.interface';

/**
 * Module-level singleton: guarantees a single initialisation across
 * StrictMode double-mounts, HMR reloads and any accidental remounts.
 */
let rumClient: AwsRum | null = null;
let initialised = false;

const ensureRumInitialised = (): void => {
  if (initialised) return;
  initialised = true;

  try {
    if (!isRumEnabled()) return;

    // Throws when a required VITE_RUM_* var is missing.
    const config = getRumConfig();

    const awsRumConfig: AwsRumConfig = {
      sessionSampleRate: 1,
      identityPoolId: config.identityPoolId,
      endpoint: config.endpoint,
      telemetries: ['performance', 'errors', 'http'],
      allowCookies: true,
      enableXRay: true,
      // Sign requests via the Cognito guest role. The identity pool's
      // default unauthenticated role is used; no explicit guestRoleArn
      // needed when the pool has one configured.
      signing: true,
      // Use the full URL (pathname + hash) as the page ID so the built-in
      // PageViewPlugin records e.g. /ui/cc-ui/#/assemblies.  The plugin runs
      // before NavigationPlugin during SDK construction, which means
      // navigation-timing and Core Web Vitals events are attributed to the
      // correct hash-based path rather than /ui/cc-ui/.
      pageIdFormat: 'PATH_AND_HASH',
    };

    rumClient = new AwsRum(
      config.applicationId,
      config.applicationVersion,
      config.region,
      awsRumConfig,
    );
  } catch (error) {
    // Initialisation must never crash the app — log once and no-op.
    // eslint-disable-next-line no-console
    console.error('RUM initialisation failed', error);
    rumClient = null;
  }
};

export const RumContext = createContext<RumContextValue>({
  recordEvent: () => undefined,
  recordError: () => undefined,
  recordPageView: () => undefined,
  isInitialized: false,
});

/**
 * Initialises AWS CloudWatch RUM exactly once and exposes a safe consumer
 * API via context. Initialisation failures are non-fatal: consumers simply
 * get no-op methods, so they never need to null-check.
 */
export const RumProvider = ({ children }: RumProviderProps) => {
  // The lazy initialiser runs once on first render, before children mount,
  // so RUM observes the earliest possible errors. The module-level guard
  // keeps it idempotent across StrictMode double-invocation.
  const [client] = useState<AwsRum | null>(() => {
    ensureRumInitialised();

    return rumClient;
  });

  const value = useMemo<RumContextValue>(
    () => ({
      recordEvent: (eventType, eventData = {}) =>
        client?.recordEvent(eventType, eventData),
      recordError: (error) => client?.recordError(error),
      recordPageView: (pageId) => client?.recordPageView(pageId),
      isInitialized: client !== null,
    }),
    [client],
  );

  return <RumContext.Provider value={value}>{children}</RumContext.Provider>;
};

/** Consumer hook for recording custom RUM telemetry. */
export const useRum = (): RumContextValue => useContext(RumContext);
